// Copyright ©️ 2026 eVolve MEP, LLC

import { type ReactNode, useMemo } from 'react';

import {
  type BendInputKey,
  bendInputKeys,
  type BendType,
  type BendTypeInput,
  type BendTypeName,
  type ConduitSizeId,
  type ConduitTypeId,
  type MachineProfile,
  type YBCRow,
} from '@evolvemep/api-ts';
import { createFormContext } from '@mantine/form';
import { v4 as uuid } from 'uuid';

import type { WithNullable } from 'helpers/helper-types';
import { isNil, isNotNil } from 'helpers/isNotNil';
import { useBendTypes } from 'modules/Shop/EvolveFabrication/BendCreator/useBendTypes';

/** `key` is a React key only; it is stripped before the request. */
export type YBCRowFormValue = WithNullable<YBCRow> & { key: string };

export type BendCreatorFormValues = {
  benderProfile: MachineProfile | null;
  bendType: BendTypeName | null;
  conduitTypeId: ConduitTypeId | null;
  conduitSizeId: ConduitSizeId | null;
  materialStockLength: number | null;
  maximizeLegLength: boolean;
  partName: string;
  quantity: number | null;
  ybcRows: YBCRowFormValue[];
} & Record<BendInputKey, number | null>;

const [BendCreatorFormProvider, useBendCreatorFormContext, useForm] = createFormContext<BendCreatorFormValues>();

/**
 * Use the bend inputs to generate a dictionary in the format:
 * {[BendInputKey]: null}
 */
export const bendInputDefaults = bendInputKeys.reduce(
  (acc, key) => ({ ...acc, [key]: null }),
  {} as Record<BendInputKey, number | null>,
);

export const toYbcRowFormValue = (row: WithNullable<YBCRow>): YBCRowFormValue => ({ ...row, key: uuid() });

export const blankYbcRow = () => toYbcRowFormValue({ straightLength: null, rotation: 0, angle: null });

export const seedYbcRows = (): YBCRowFormValue[] => [blankYbcRow(), { ...blankYbcRow(), angle: 0 }];

export const validateQuantity = (v: number | null) => {
  if (isNil(v)) return 'Quantity is required';
  if (v <= 0) return 'Quantity must be 1 or more';
  if (v > 99) return "Quantity can't be greater than 99";
  return false;
};

/**
 * Each bend has its own required fields and may be an angle or a dimension.
 * this validates the bend input based on the bend type configuration
 */
const validateBendWithBendType = (v: number | null, inputData: BendTypeInput) => {
  if (isNil(v)) {
    if (inputData.isRequired) return `${inputData.name} is required`;
    return false;
  }
  if (inputData.isAngle && v > 90) return `${inputData.name} must be less than or equal to 90°`;
  return false;
};

/**
 * Use the bend type data to generate validators specific to the user's selected bend type.
 */
export const getBendValidators = (bendTypeData: BendType[]) =>
  bendInputKeys.reduce(
    (acc, key) => {
      acc[key] = (v: number | null, fv: BendCreatorFormValues) => {
        if (isNil(bendTypeData)) return 'Bend type data is loading';
        const bendType = bendTypeData.find((bt) => bt.name === fv.bendType);
        const bendTypeInput = bendType?.inputs.find((i) => i.keyName === key);
        if (isNil(bendTypeInput)) return false;
        return validateBendWithBendType(v, bendTypeInput);
      };
      return acc;
    },
    {} as Record<BendInputKey, (v: number | null, fv: BendCreatorFormValues) => string | false>,
  );

type Props = {
  initialValues?: BendCreatorFormValues;
  children: ReactNode;
};

export const BendCreatorFormContext = ({ children, initialValues }: Props) => {
  const { bendTypes } = useBendTypes();

  const bendTypeValidators = useMemo(() => (isNotNil(bendTypes) ? getBendValidators(bendTypes) : {}), [bendTypes]);

  const form = useForm({
    initialValues: initialValues ?? {
      benderProfile: null,
      bendType: null,
      conduitTypeId: null,
      conduitSizeId: null,
      materialStockLength: 10,
      maximizeLegLength: false,
      partName: '',
      quantity: 1,
      ybcRows: [],
      ...bendInputDefaults,
    },
    validate: {
      benderProfile: (v) => !v && 'Bender profile is required',
      bendType: (v) => !v && 'Bend type is required',
      conduitTypeId: (v) => !v && 'Conduit type is required',
      conduitSizeId: (v) => !v && 'Conduit size is required',
      partName: (v) => !v && 'Part name is required',
      materialStockLength: (v) => !v && 'Material stock length is required',
      quantity: validateQuantity,
      ybcRows: {
        straightLength: (v) => (isNil(v) ? 'Length is required' : v <= 0 && 'Length must be greater than 0'),
        rotation: (v) => isNil(v) && 'Rotation is required',
        angle: (v) => isNil(v) && 'Angle is required',
      },
      ...bendTypeValidators,
    },
  });

  return <BendCreatorFormProvider form={form}>{children}</BendCreatorFormProvider>;
};

export { useBendCreatorFormContext };
